// Courses catalogue page with live stats from API
import { useEffect, useState } from 'react'
import { BookOpen, Award, Users, TrendingUp } from 'lucide-react'

import { useClassSessionStore, DEFAULT_PASSING_SCORE } from '@/features/classes/stores'

import { ComingSoonBanner } from './components/ComingSoonBanner'
import { CourseCard } from './components/CourseCard'
import { CourseDetail, type CourseStats } from './components/CourseDetail'

export function CoursesPage() {
  const [selected, setSelected] = useState<string | null>(null)
  const { sessions, attendees, certificates, courses, loadCourses } = useClassSessionStore()

  useEffect(() => {
    void loadCourses()
  }, [loadCourses])

  const activeCourses = courses.filter((c) => c.isActive !== false)

  const courseStats: CourseStats[] = activeCourses.map((course) => {
    const courseSessions = sessions.filter((s) => s.courseId === course.slug)
    const sessionIds = new Set(courseSessions.map((s) => s.id))
    const courseAttendees = attendees.filter((a) => sessionIds.has(a.sessionId))
    const inProgress = courseAttendees.filter((a) => a.status !== 'completed').length
    const scored = courseAttendees.filter((a) => a.quizScore !== null)
    const passed = scored.filter((a) => (a.quizScore ?? 0) >= DEFAULT_PASSING_SCORE)
    const passRate = scored.length > 0 ? Math.round((passed.length / scored.length) * 100) : null

    const attendeeIds = new Set(courseAttendees.map((a) => a.id))
    const issuedCerts = certificates.filter((c) => attendeeIds.has(c.attendeeId)).length

    return {
      courseId: course.slug,
      enrolled: courseAttendees.length,
      completed: passed.length,
      inProgress,
      certificatesIssued: issuedCerts,
      passRate,
    }
  })

  const statsMap = Object.fromEntries(courseStats.map((s) => [s.courseId, s]))

  const totalStudents   = courseStats.reduce((a, s) => a + s.enrolled, 0)
  const totalCerts      = courseStats.reduce((a, s) => a + s.certificatesIssued, 0)
  const totalInProgress = courseStats.reduce((a, s) => a + s.inProgress, 0)
  const ratedCourses    = courseStats.filter((s) => s.passRate !== null)
  const avgPassRate     = ratedCourses.length > 0
    ? Math.round(ratedCourses.reduce((a, s) => a + (s.passRate ?? 0), 0) / ratedCourses.length)
    : 0

  return (
    <div className="space-y-5">
      <ComingSoonBanner />

      <div>
        <h2 className="text-xl font-bold text-navy">Course Catalog</h2>
        <p className="text-sm text-slate-500">Training catalog management and statistics</p>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Courses',   value: activeCourses.length, color: 'text-navy', bg: 'bg-white',      icon: BookOpen,   sub: 'in active catalog' },
          { label: 'Active Students',   value: totalInProgress,   color: 'text-primary', bg: 'bg-orange-50',  icon: Users,      sub: 'in training now' },
          { label: 'Certificates Issued', value: totalCerts,        color: 'text-green-600',  bg: 'bg-green-50',   icon: Award,      sub: `of ${totalStudents} students` },
          { label: 'Average Pass Rate',         value: avgPassRate > 0 ? `${avgPassRate}%` : '—', color: 'text-slate-500', bg: 'bg-gray-50', icon: TrendingUp, sub: 'general pass rate' },
        ].map((s) => {
          const Icon = s.icon
          return (
            <div key={s.label} className={`${s.bg} border border-slate-150 rounded-xl p-4`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-slate-500 font-medium">{s.label}</p>
                <Icon className={`w-4 h-4 ${s.color}`} />
              </div>
              <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.sub}</p>
            </div>
          )
        })}
      </div>

      {/* Course grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {activeCourses.map((course) => (
          <CourseCard
            key={course.slug}
            course={course}
            stats={statsMap[course.slug]}
            onClick={() => setSelected(course.slug)}
          />
        ))}
      </div>

      {/* Detail modal */}
      {selected && (
        <CourseDetail
          course={activeCourses.find((c) => c.slug === selected)!}
          stats={statsMap[selected]}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}
